import type {
  FeedbackScreenshotLink,
  FeedbackScreenshotUpload,
  MyFeedbackItem,
  SendFeedbackRequest,
  SendFeedbackResponse,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { csrf } from "../account/queries.ts";
import { api } from "../api.ts";
import { apiError, ok } from "../api-errors.ts";
import { putStorageFile } from "../projects/storage-put.ts";
import { useSession } from "../session.tsx";

export const feedbackKeys = {
  mine: ["me", "feedback"] as const,
  /** Outside `["me"]`: a send or a session refresh never re-downloads an image. */
  screenshot: (reportId: string) => ["feedback-screenshot", reportId] as const,
};

/** A send refused for the hour (`429`): when the next one is accepted. */
export class FeedbackThrottled extends Error {
  constructor(readonly retryAt: Date) {
    super("throttled");
    this.name = "FeedbackThrottled";
  }
}

/** What a send did: the report, and whether its screenshot (when one was added) is attached. */
export interface FeedbackSent extends SendFeedbackResponse {
  screenshot: "attached" | "failed" | "none";
}

/**
 * Attaches a screenshot to a report just sent (design D4): the upload URL, the PUT straight to
 * storage ({@link putStorageFile}: no cookie), then the confirm. Throws on any failure: the report stays sent. An API
 * without screenshots answers `404`, which fails the same way.
 */
export async function attachScreenshot(
  reportId: string,
  blob: Blob,
  unmasked: boolean,
  csrfToken: string,
): Promise<void> {
  const routes = api.api.feedback[":reportId"].screenshot;
  const target = await ok(
    await routes.$post({ param: { reportId }, json: { sizeBytes: blob.size } }, csrf(csrfToken)),
  );
  const { upload } = target as { upload: FeedbackScreenshotUpload };
  // The shared storage transport: the signed headers only, no cookie, and a create-only `412`
  // is the object an earlier PUT of this same URL already stored.
  await putStorageFile(
    { method: "PUT", url: upload.url, headers: upload.headers, expiresAt: upload.expiresAt },
    new File([blob], `feedback-${reportId}.png`, { type: blob.type || "image/png" }),
  );
  await ok(
    await routes.confirm.$post({ param: { reportId }, json: { unmasked } }, csrf(csrfToken)),
  );
}

/**
 * A sent report, then its screenshot when one was added: a failed attach (an upload error, or
 * an API without screenshots answering `404`) never fails the send, the result says so.
 */
export async function withScreenshot(
  sent: SendFeedbackResponse,
  screenshot: { blob: Blob; unmasked: boolean } | null | undefined,
  csrfToken: string,
): Promise<FeedbackSent> {
  if (!screenshot) {
    return { ...sent, screenshot: "none" };
  }
  try {
    await attachScreenshot(sent.id, screenshot.blob, screenshot.unmasked, csrfToken);
    return { ...sent, screenshot: "attached" };
  } catch {
    return { ...sent, screenshot: "failed" };
  }
}

/** A 60-second URL of one of the caller's own screenshots. */
export async function myScreenshotLink(reportId: string): Promise<FeedbackScreenshotLink> {
  return ok(await api.api.me.feedback[":reportId"].screenshot.$get({ param: { reportId } }));
}

/**
 * `POST /api/feedback`, then the screenshot when one was added; a `429` becomes
 * {@link FeedbackThrottled} with its `Retry-After`. A screenshot that fails to attach never
 * fails the send: the result says so.
 */
export function useSendFeedback() {
  const queryClient = useQueryClient();
  const { csrfToken } = useSession();
  return useMutation({
    mutationFn: async ({
      body,
      screenshot,
    }: {
      body: SendFeedbackRequest;
      screenshot?: { blob: Blob; unmasked: boolean } | null;
    }): Promise<FeedbackSent> => {
      const res = await api.api.feedback.$post({ json: body }, csrf(csrfToken));
      // Typed by the route's own answers (201, 400): the limit's 429 comes from the error handler.
      if ((res.status as number) === 429) {
        const seconds = Number(res.headers.get("retry-after"));
        throw new FeedbackThrottled(
          new Date(Date.now() + (Number.isFinite(seconds) && seconds > 0 ? seconds : 3600) * 1000),
        );
      }
      if (!res.ok) {
        const error = await apiError(res);
        if (error.code === "feedback_disabled") {
          // Turned off since /api/me was read: hide the entry points.
          void queryClient.invalidateQueries({ queryKey: ["me"] });
        }
        throw error;
      }
      return withScreenshot((await res.json()) as SendFeedbackResponse, screenshot, csrfToken);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: feedbackKeys.mine }),
  });
}

/** The caller's own reports ("My feedback"), newest first. */
export function useMyFeedback() {
  return useQuery({
    queryKey: feedbackKeys.mine,
    queryFn: async (): Promise<MyFeedbackItem[]> =>
      (await ok(await api.api.me.feedback.$get())).reports,
  });
}
